import { useId } from 'react';
import type { CSSProperties } from 'react';
import { getMinimapView, getRectOfNodes } from '../../core';
import type { Node } from '../../core';
import { sameRects, selectNodeRects, selectPaneSize, selectViewport, useMindmapStore } from '../../store';
import type { MindmapState } from '../../store';
import { classNames } from '../../utils';
import { maskPath } from './maskPath';

/** v11 defaults. */
const DEFAULT_WIDTH = 200;
const DEFAULT_HEIGHT = 150;
const DEFAULT_NODE_COLOR = '#e2e2e2';
const DEFAULT_MASK_COLOR = 'rgb(240, 240, 240, 0.6)';
const DEFAULT_NODE_BORDER_RADIUS = 5;
const NODE_STROKE_WIDTH = 2;
const DEFAULT_MASK_STROKE_WIDTH = 1;

/** A colour for every node, or one per node (v11). */
type NodeColor = string | ((node: Node) => string);

export type MiniMapProps = {
  nodeColor?: NodeColor;
  nodeStrokeColor?: NodeColor;
  /** Corner radius of the node boxes, flow units (v11). */
  nodeBorderRadius?: number;
  /** Fill outside the visible area. */
  maskColor?: string;
  /** Outline of the visible area (v11 names; ours outlines only the visible area, not the outer frame). */
  maskStrokeColor?: string;
  maskStrokeWidth?: number;
  /** Size of the overview in px. */
  width?: number;
  height?: number;
  className?: string;
  style?: CSSProperties;
};

const selectNodeLookup = (state: MindmapState) => state.nodeLookup;

const colorOf = (color: NodeColor, node: Node | undefined): string =>
  typeof color === 'function' ? (node ? color(node) : DEFAULT_NODE_COLOR) : color;

/** v11 `<MiniMap>`: a static overview of all nodes and the visible area, bottom right. */
export const MiniMap = ({
  nodeColor = DEFAULT_NODE_COLOR,
  nodeStrokeColor = 'transparent',
  nodeBorderRadius = DEFAULT_NODE_BORDER_RADIUS,
  maskColor = DEFAULT_MASK_COLOR,
  maskStrokeColor,
  maskStrokeWidth = DEFAULT_MASK_STROKE_WIDTH,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
  className,
  style,
}: MiniMapProps) => {
  const rects = useMindmapStore(selectNodeRects, sameRects);
  const nodeLookup = useMindmapStore(selectNodeLookup);
  const viewport = useMindmapStore(selectViewport);
  const paneSize = useMindmapStore(selectPaneSize);
  const titleId = `cm-minimap-title-${useId()}`;
  const nodesRect = rects.length > 0 ? getRectOfNodes(rects.map(({ id, x, y, width: w, height: h }) => ({ id, data: {}, position: { x, y }, width: w, height: h }))) : null;
  const { viewBox, visibleRect, margin } = getMinimapView(nodesRect, viewport, paneSize, { width, height });
  // Keeps the outline as thick as asked on screen, whatever the overview's scale.
  const strokeScale = viewBox.width / width;

  return (
    <div className={classNames('cm-panel', 'cm-minimap', 'bottom', 'right', className)} style={style}>
      <svg width={width} height={height} viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`} role="img" aria-labelledby={titleId}>
        <title id={titleId}>Mind map overview</title>
        {rects.map(({ id, x, y, width: w, height: h }) => {
          const node = nodeLookup.get(id)?.node;
          return (
            <rect
              key={id}
              className="cm-minimap-node"
              x={x}
              y={y}
              width={w}
              height={h}
              rx={nodeBorderRadius}
              ry={nodeBorderRadius}
              fill={colorOf(nodeColor, node)}
              stroke={colorOf(nodeStrokeColor, node)}
              strokeWidth={NODE_STROKE_WIDTH}
            />
          );
        })}
        <path className="cm-minimap-mask" d={maskPath(viewBox, visibleRect, margin)} fill={maskColor} fillRule="evenodd" />
        {maskStrokeColor && (
          <rect
            className="cm-minimap-viewport"
            {...visibleRect}
            fill="none"
            stroke={maskStrokeColor}
            strokeWidth={maskStrokeWidth * strokeScale}
            rx={nodeBorderRadius}
          />
        )}
      </svg>
    </div>
  );
};
